import { CSS } from "./styles.ts";
import { mountConsent, effectiveConsent, gpcOn, loadScript, type Choice } from "../consent.ts";
import { fireBrowserEvent, cookie } from "../pixel/browser.ts";
import { esc, hotBlock } from "./parts.ts";

interface Choice { id: string; label: string }
interface Question { id: string; text: string; kind: "choice" | "text"; optional: boolean; hint: string; choices: Choice[] }
interface Contact { primary: "mobile"; prompt: string; note: string }
interface Config { hook: string; lead: string; total?: number; questions: Question[]; contact?: Contact | null; sender?: string; sends_email?: boolean; testing?: boolean }
interface Variant { hook?: string; lead?: string; questions?: Record<string, { text?: string; choices?: Record<string, string> }> }
interface State { visitId: string; answers: Record<string, string>; index: number; done?: boolean; contact?: boolean; returning?: boolean; close?: number; variant?: Variant; forName?: string }

// ABSPLIT1: merge this visit's B overrides onto the served config; a state with no variant stored is A.
function applyVariant(cfg: Config, variant: Variant | undefined): Config {
  if (!variant) return cfg;
  const c: Config = { ...cfg, hook: variant.hook ?? cfg.hook, lead: variant.lead ?? cfg.lead };
  if (variant.questions) {
    c.questions = cfg.questions.map((q) => {
      const o = variant.questions![q.id];
      if (!o) return q;
      return { ...q, text: o.text ?? q.text, choices: o.choices ? q.choices.map((ch) => ({ ...ch, label: o.choices![ch.id] ?? ch.label })) : q.choices };
    });
  }
  return c;
}

// sure_line stays server-side (public config omits it); 0.8 matches the seeded quizzes.
const SURE_LINE = 0.8;
// SORTEDSMS1: the text-terms note's Privacy and Terms links wear the cookie bar's link look (styles.ts `.consent a`:
// ink, underlined, a 48 px tall tap area that moves nothing), so a link here never falls back to the browser's blue;
// the pair wraps as one ("Privacy · Terms", never "Terms" alone on the last line at 390).
const NOTE_LINKS = `.r__note a{color:var(--s-text);text-decoration:underline;display:inline-block;padding:14px 4px;margin:-14px -4px}.r__note span{white-space:nowrap}`;
const BACK = `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M10 3 5 8l5 5"/></svg>`;

const ctaButton = (d: any) => `<a class="btn btn--primary btn--wide" href="${esc(d.cta_url || "#")}" target="_blank" rel="noopener">${esc(d.cta_label)}</a>${d.cta_note ? `<p class="r__note">${esc(d.cta_note)}</p>` : ""}`;
const notifyBlock = (d: any) => d.has_email ? `<p class="r__pick">${esc(d.route.saved || "")}</p>`
  : `<form class="q__form" data-notify novalidate><label class="q__field">${esc(d.route.label)}<input class="q__input" name="email" type="email" autocomplete="email"></label><p class="q__err" role="alert"></p><button class="btn btn--primary" type="submit">${esc(d.route.button)}</button></form>`;
const shareBlock = (d: any) => `<div class="r__book"><p class="r__pick">${esc(d.route.label)}</p><button class="btn btn--primary btn--wide" type="button" data-share>${esc(d.route.button)}</button></div>`;
const ctaFor = (d: any) => d.route?.kind === "notify" ? notifyBlock(d) : d.route?.kind === "share" ? shareBlock(d) : (d.hot && d.booking_url ? hotBlock(d) : ctaButton(d));

async function boot(host: HTMLElement, apiBase = ""): Promise<void> {
  const slug = host.dataset.slug!;
  const key = `sorted:${slug}`;
  const rawCfg: Config = await (await fetch(`${apiBase}/api/quiz/${slug}`)).json();
  let cfg: Config = rawCfg;
  let qs = cfg.questions;
  const sender = cfg.sender ? `<p class="q__sender">${esc(cfg.sender)}</p>` : "";
  const root = host.attachShadow({ mode: "open" });
  root.innerHTML = `<style>${CSS}${NOTE_LINKS}</style><div class="root"></div>`;
  const out = root.querySelector(".root") as HTMLElement;

  let st: State = { visitId: "", answers: {}, index: 0 };
  const hadVisit = (() => { try { return !!JSON.parse(localStorage.getItem(key) || "{}").visitId; } catch { return false; } })();
  try { st = { ...st, ...JSON.parse(localStorage.getItem(key) || "{}") }; } catch { /* fresh */ }
  st.returning = hadVisit;
  const firstOpen = qs.findIndex((q) => !(q.id in st.answers));
  st.index = firstOpen === -1 ? qs.length : firstOpen;
  const save = () => localStorage.setItem(key, JSON.stringify(st));
  let welcome = false;
  let contactEmail = '';

  // A stored visit the server no longer knows starts fresh; a finished one goes straight to its result.
  async function checkStored(): Promise<void> {
    if (!st.visitId) return;
    try {
      const r = await fetch(`${apiBase}/api/visit/${st.visitId}/result`);
      if (r.status === 404) {
        const e = await r.json().catch(() => ({}));
        if (e?.error === "bad_visit") { localStorage.removeItem(key); st = { visitId: "", answers: {}, index: 0, returning: false }; return; }
        return;
      }
      if (r.ok) { st.done = true; st.contact = true; return; }
    } catch { /* offline: resume from local answers */ }
  }
  function startOver(): void {
    const old = st.visitId;
    if (old) void fetch(`${apiBase}/api/visit/${old}/restart`, { method: "POST" }).catch(() => {});
    localStorage.removeItem(key);
    st = { visitId: "", answers: {}, index: 0, returning: true }; welcome = false;
    render();
    void ev("view"); // new visit created at render of question 1
  }

  // One create in flight at a time: at question 1 the view and reach events fire together and must share one visit (B56).
  let visitP: Promise<void> | null = null;
  function ensureVisit(): Promise<void> {
    if (st.visitId) return Promise.resolve();
    if (!visitP) visitP = (async () => {
      const r = await fetch(`${apiBase}/api/visit`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ slug, attribution: Object.fromEntries(new URLSearchParams(location.search)), landing_path: location.pathname, referrer: document.referrer, returning: st.returning === true, invite: new URLSearchParams(location.search).get("s") || undefined }) });
      const d = await r.json();
      st.visitId = d.visitId;
      // ABSPLIT1: variant arrives once, at visit creation; a stored entry without one is A.
      if (d.variant && Object.keys(d.variant).length) { st.variant = d.variant; cfg = applyVariant(rawCfg, st.variant); qs = cfg.questions; }
      // WEBLINK1: an invite link's landing carries the name it was made for.
      if (d.invite?.name) st.forName = String(d.invite.name).slice(0, 80);
      save();
    })().finally(() => { visitP = null; });
    return visitP;
  }
  async function ev(stage: string, questionId?: string): Promise<void> {
    try { await ensureVisit(); await fetch(`${apiBase}/api/visit/${st.visitId}/event`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ stage, questionId }) }); } catch { /* beacon is best-effort */ }
  }
  async function answer(q: Question, value: string): Promise<void> {
    const isFirst = Object.keys(st.answers).length === 0;
    welcome = false; st.answers[q.id] = value; st.index += 1; save(); render();
    try {
      await ensureVisit();
      if (isFirst) void ev("start");
      void ev("answer", q.id);
      const r = await fetch(`${apiBase}/api/visit/${st.visitId}/answer`, { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify({ questionId: q.id, value }) });
      const res = await r.json();
      if (typeof res.prob === "number") { st.close = Math.min(0.95, res.prob / SURE_LINE); save(); render(); }
      if (res.done) { st.done = true; st.close = 1; save(); render(); }
      else if (res.next) { const i = qs.findIndex((x) => x.id === res.next); if (i >= 0 && i !== st.index) { st.index = i; save(); render(); } }
    } catch { /* localStorage keeps the answer; server copy retried on next answer */ }
  }
  // B10: a skip is stored as an empty answer so the server counts the text question as answered.
  function skip(q: Question): void { void answer(q, ""); }
  function back(): void { st.index = Math.max(0, st.index - 1); save(); render(); }

  async function showResult(): Promise<void> {
    try {
      await ensureVisit();
      const r = await fetch(`${apiBase}/api/visit/${st.visitId}/result`);
      if (r.status === 404) { st.done = false; st.contact = false; const i = qs.findIndex((q) => !(q.id in st.answers)); st.index = i === -1 ? 0 : i; save(); render(); return; }
      if (!r.ok) throw new Error("result");
      const d = await r.json();
      if (contactEmail) d.has_email = true;
      const said = d.own_words ? `<p class="r__said">You said: <q>${esc(d.own_words)}</q></p>` : "";
      const num = d.number ? `<div class="r__number"><p class="r__figure">${esc(d.number)}</p><p class="r__label">${esc(d.number_label)}</p>${d.number2 ? `<p class="r__label r__label--second">${esc(d.number2)}</p>` : ""}${d.assumptions ? `<details class="r__how"><summary>How we got this number</summary><p class="r__basis">${esc(d.assumptions)}</p></details>` : ""}</div>` : "";
      out.innerHTML = `<main class="r"><div class="r__main"><div class="r__col">${sender}<div><p class="r__type">Your type</p><h1 class="r__name">${esc(d.name)}</h1></div>${said}${num}
        <div class="r__cta" data-cta>${ctaFor(d)}<button class="q__skip" type="button" data-startover>Start over</button></div></div></div>
        <section class="r__proof"><blockquote class="r__quote"><p>${esc(d.proof.quote)}</p><footer class="r__who">${esc(d.proof.who)}</footer></blockquote></section></main>`;
      out.querySelector("[data-startover]")?.addEventListener("click", startOver);
      // Hot visitor: the quiet link swaps the calendar for the normal button path.
      out.querySelector("[data-nocal]")?.addEventListener("click", () => {
        const b = out.querySelector("[data-book]") as HTMLElement | null;
        if (b) b.outerHTML = ctaButton(d);
      });
      const nf = out.querySelector("[data-notify]") as HTMLFormElement | null;
      nf?.addEventListener("submit", async (e) => {
        e.preventDefault();
        const email = String((nf.querySelector("input[name=email]") as HTMLInputElement).value || "").trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { (nf.querySelector(".q__err") as HTMLElement).textContent = "Enter a valid email."; return; }
        await fetch(`${apiBase}/api/visit/${st.visitId}/lead`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ name: "", email, phone: "" }) });
        nf.outerHTML = `<p class="r__pick">${esc(d.route.saved || "")}</p>`;
      });
      const sb = out.querySelector("[data-share]") as HTMLElement | null;
      sb?.addEventListener("click", () => {
        const btn = sb;
        navigator.clipboard.writeText(location.href.split("?")[0]).then(() => { btn.textContent = d.route.copied || ""; }, () => { btn.outerHTML = `<input class="q__input" readonly value="${esc(location.href.split("?")[0])}"><p class="r__basis">Press Ctrl+C to copy.</p>`; (out.querySelector(".r__book input") as HTMLInputElement | null)?.select(); });
      });
    } catch {
      out.innerHTML = `<main class="r"><div class="r__main"><div class="r__col"><p class="r__name">We couldn't load your result.</p><div class="r__cta"><button class="btn btn--primary btn--wide" data-retry>Try again</button></div></div></div></main>`;
      out.querySelector("[data-retry]")?.addEventListener("click", () => { void showResult(); });
    }
  }

  function render(): void {
    if ((st.done || st.index >= qs.length) && !st.contact) {
      const mob = cfg.contact?.primary === "mobile";
      const fields = mob
        ? `<label class="q__field">First name<input class="q__input" name="name" autocomplete="given-name"></label>
        <label class="q__field">Mobile number<input class="q__input" name="phone" type="tel" inputmode="tel" autocomplete="tel" required></label>
        <label class="q__field">Email (optional)<input class="q__input" name="email" type="email" autocomplete="email"></label>`
        : `<label class="q__field">First name<input class="q__input" name="name" autocomplete="given-name"></label>
        <label class="q__field">Email<input class="q__input" name="email" type="email" autocomplete="email" required></label>
        <label class="q__field">Mobile (optional)<input class="q__input" name="phone" type="tel" autocomplete="tel"></label>`;
      const title = mob ? esc(cfg.contact!.prompt) : cfg.sends_email ? "Where should we send your result?" : "Your result is ready. Where can we reach you?";
      const sub = mob ? esc(cfg.contact!.note) : cfg.sends_email ? "Your result is ready. Tell us where to send it." : "";
      // SORTEDSMS1 (FOOTER-AND-SMS-STANDARD.md §2 "an unticked consent box (web)", Twilio 30909): under the mobile field's
      // group on every contact step (both kinds show a mobile field), the text terms are the label of one checkbox that is
      // never pre-ticked and never required, with links to Sorted's Privacy and Terms (new tab; a tap on a link opens it
      // and leaves the box alone). Its checked state rides the lead POST as `sms_consent`; unticked = no text is queued.
      // The texting business is the sender's brand ("<brand> · <title>" → "<brand>").
      // Fix 5 (judge SHEET GAP 2): a quiz whose own note above states how often it texts ("Grant texts once a day.")
      // keeps that one frequency at the opt-in; "Message frequency varies." shows only when the note names none.
      const often = /\b(once|twice|daily|weekly|monthly|(a|per|every) (day|week|month)|\d+ (texts?|messages?))\b/i.test(cfg.contact?.note ?? "");
      const texter = (cfg.sender || "").split(" · ")[0] || "this business";
      const terms = `<label class="q__check"><input class="q__box" type="checkbox" name="sms_consent"><span class="r__note">Yes, text me. ${esc(texter)} may text me about my result.${often ? "" : " Message frequency varies."} Message and data rates may apply. Reply STOP to cancel, HELP for help. Consent is not a condition of any purchase. <span><a href="${esc(apiBase)}/privacy" target="_blank" rel="noopener">Privacy</a> · <a href="${esc(apiBase)}/terms" target="_blank" rel="noopener">Terms</a></span></span></label>`;
      out.innerHTML = `<main class="q">${sender}<div class="q__top"><span></span></div><div class="q__body"><div class="q__head"><div class="q__prog"><p class="q__count">Last step</p><span class="q__bar"><i style="width:100%"></i></span></div><h1 class="q__question q__question--solo">${title}</h1>${sub ? `<p class="q__lead">${sub}</p>` : ""}</div>
        <form class="q__form" novalidate>${fields}${terms}
        <p class="q__err" role="alert"></p><button class="btn btn--primary" type="submit">Show my result</button><button class="q__skip" type="button" data-skipc>Skip</button></form></div></main>`;
      void ev("contact_shown");
      const form = out.querySelector("form") as HTMLFormElement;
      const finish = () => { st.contact = true; save(); render(); };
      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        const f = new FormData(form), email = String(f.get("email") || "").trim();
        const phone = String(f.get("phone") || "").trim();
        const badEmail = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        if (mob ? phone.replace(/\D/g, "").length < 10 || (email !== "" && badEmail) : badEmail) { (out.querySelector(".q__err") as HTMLElement).textContent = mob ? "Enter a mobile number we can text." : "Enter a valid email."; return; }
        contactEmail = email;
        finish();
        try {
          await ensureVisit();
          const consent = effectiveConsent((() => { try { return localStorage.getItem("sorted:consent") as Choice | null; } catch { return null; } })(), gpcOn(navigator as { globalPrivacyControl?: boolean }));
          const ck = consent === "accepted";
          await fetch(`${apiBase}/api/visit/${st.visitId}/lead`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ name: f.get("name"), email, phone, sms_consent: f.has("sms_consent"), ...(ck ? { page_url: location.href, fbp: cookie("_fbp"), fbc: cookie("_fbc") } : {}) }) });
          void fireBrowserEvent(apiBase, st.visitId, "Lead", consent);
        } catch { /* lead retried never; visitor already moved on */ }
      });
      out.querySelector("[data-skipc]")?.addEventListener("click", finish);
      return;
    }
    if (st.done || st.index >= qs.length) {
      void ev("result");
      out.innerHTML = `<main class="r"><div class="r__main"><p class="r__basis" role="status">Working out your result…</p></div></main>`;
      void showResult();
      return;
    }
    const q = qs[st.index], first = st.index === 0;
    void ev("reach", q.id);
    const m = cfg.total ?? qs.length, n = Math.min(m, Object.keys(st.answers).filter((id) => id !== q.id).length + 1); const prog = `<div class="q__prog"><p class="q__count">Question ${n} of ${m}</p><span class="q__bar"><i style="width:${Math.round((n / m) * 100)}%"></i></span></div>`;
    const top = `<div class="q__top">${first ? "<span></span>" : `<a class="q__back" href="#" data-back>${BACK}Back</a>`}</div>`;
    const prev = st.answers[q.id];
    let body: string;
    if (q.kind === "text") {
      body = `<div class="q__head">${prog}<h1 class="q__question q__question--solo">${esc(q.text)}</h1>${q.hint ? `<p class="q__lead">${esc(q.hint)}</p>` : ""}</div>
        <section class="q__ask"><textarea class="q__text" aria-label="Your words">${esc(prev ?? "")}</textarea>
        <div class="q__actions"><button class="btn btn--ghost" data-skip>Skip</button><button class="btn btn--primary" data-next>Next</button></div></section>`;
    } else {
      const choices = q.choices.map((c) => `<button class="choice" type="button" role="radio" data-c="${esc(c.id)}" aria-checked="${prev === c.id}">${esc(c.label)}</button>`).join("");
      const head = first
        ? `<div class="q__head">${st.forName ? `<p class="q__for">For ${esc(st.forName)}</p>` : ""}<h1 class="q__hook">${esc(cfg.hook)}</h1><p class="q__lead">${esc(cfg.lead)}</p></div><section class="q__ask">${prog}<h2 class="q__question" id="q-text">${esc(q.text)}</h2><div class="q__choices" role="radiogroup" aria-labelledby="q-text">${choices}</div></section>`
        : `<div class="q__head">${prog}<h1 class="q__question q__question--solo" id="q-text">${esc(q.text)}</h1></div><section class="q__ask"><div class="q__choices" role="radiogroup" aria-labelledby="q-text">${choices}</div></section>`;
      body = head;
    }
    const wb = welcome ? `<div class="q__welcome"><p class="r__basis">Welcome back — you're partway through.</p><button class="q__skip" type="button" data-startover>Start over</button></div>` : "";
    out.innerHTML = `<main class="q">${sender}${top}<div class="q__body">${wb}${body}</div></main>`;
    out.querySelector("[data-startover]")?.addEventListener("click", startOver);
    out.querySelector("[data-back]")?.addEventListener("click", (e) => { e.preventDefault(); back(); });
    out.querySelectorAll<HTMLElement>("[data-c]").forEach((b) => b.addEventListener("click", () => answer(q, b.dataset.c!)));
    out.querySelector("[data-skip]")?.addEventListener("click", () => skip(q));
    out.querySelector("[data-next]")?.addEventListener("click", () => {
      const v = (out.querySelector(".q__text") as HTMLTextAreaElement).value.trim();
      v ? answer(q, v) : skip(q);
    });
  }
  await checkStored();
  // ABSPLIT1: a live test on the hook or first question must settle before the first render, or the visitor sees A flash to B.
  const invited = /^[0-9a-f]{12}$/.test(new URLSearchParams(location.search).get("s") ?? "");
  if ((cfg.testing || invited) && !st.done) await ensureVisit();
  if (st.variant) { cfg = applyVariant(rawCfg, st.variant); qs = cfg.questions; }
  const fo = qs.findIndex((q) => !(q.id in st.answers));
  st.index = fo === -1 ? qs.length : fo;
  welcome = !!st.visitId && !st.done && Object.keys(st.answers).length > 0 && st.index < qs.length;
  render();
  if (!st.done) void ev("view"); // visit exists from render; first answer reuses it
  mountConsent(root, { slug, apiBase, tags: (cfg as { tags?: { name: string; src: string }[] }).tags ?? [], visitId: () => st.visitId, ensureVisit });
  // WHO1: Snitcher's tracker is not an ad tag — it loads at once for every visitor, no bar, no consent record;
  // a browser sending the do-not-track signal (GPC) is skipped. The id check is ID_OK's (src/pixel/server.ts).
  const sn = (cfg as { snitcher?: unknown }).snitcher;
  if (typeof sn === "string" && /^[A-Za-z0-9_-]{3,40}$/.test(sn) && !gpcOn(navigator as { globalPrivacyControl?: boolean })) loadScript({ name: "snitcher", src: "/pixel/snitcher.js?id=" + sn }, apiBase);
}

export function mount(host: HTMLElement, opts: { apiBase?: string } = {}): Promise<void> {
  return boot(host, opts.apiBase ?? "");
}

const el = document.getElementById("sorted-quiz");
if (el) void mount(el);
